/* eslint-disable deprecation/deprecation */
import React, { useEffect, useMemo, useState } from 'react';
import { makeStyles, FormControl, NativeSelect, InputLabel } from '@material-ui/core';
import { useTechDocsReaderPage } from '@backstage/plugin-techdocs-react';
import { useNavigate } from 'react-router-dom';

import { useVersions, VersionInfo } from './useVersions';

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(1.5, 2, 1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  control: {
    width: '140px'
  }
}));

type VersionOption = { value: string; label: string; isLatest?: boolean };

/**
 * TechDocs Addon: Version Selector using MUI Select
 *
 * Uses MUI Select for accessibility and removes custom dropdown/CSS.
 * Navigates via React Router when version changes.
 *
 * @public
 */
export const VersionSelector = () => {
  const classes = useStyles();
  const [selected, setSelected] = useState<string>('');

  // Router push helper (aligns with requirement "router.push(newPath)")
  const navigate = useNavigate();
  const router = React.useMemo(() => ({ push: (path: string) => navigate(path) }), [navigate]);

  // Versions from backend
  const { value: versionsData, loading, error } = useVersions();

  // TechDocs context (fallback for path building)
  const techDocsPage = useTechDocsReaderPage();

  // Map to options
  const versions: VersionOption[] = useMemo(
    () =>
      versionsData
        ? versionsData.map((v: VersionInfo) => ({
            value: v.version,
            label: v.title || v.version,
            isLatest: v.aliases?.includes('latest'),
          }))
        : [],
    [versionsData],
  );

  // Initial selection from URL
  useEffect(() => {
    if (versions.length > 0 && !selected) {
      const currentPath = window.location.pathname;
      const pathSegments = currentPath.split('/').filter(Boolean);
      if (pathSegments.length >= 5 && pathSegments[0] === 'docs') {
        const urlVersion = pathSegments[4];
        const matchingVersion = versions.find(v => v.value === urlVersion);
        if (matchingVersion) {
          setSelected(urlVersion);
          return;
        }
      }
      setSelected(versions[0].value);
    }
  }, [versions, selected]);

  // Do not render if no versions available
  if (loading || error || !versionsData || versions.length === 0) {
    return null;
  }

  const handleVersionChange = (versionValue: string) => {
    setSelected(versionValue);

    const currentPath = window.location.pathname;
    const pathSegments = currentPath.split('/').filter(Boolean);

    if (pathSegments.length >= 4 && pathSegments[0] === 'docs') {
      const [docs, namespace, kind, name] = pathSegments;
      const newPath = `/${docs}/${namespace}/${kind}/${name}/${versionValue}/`;
      router.push(newPath);
    } else {
      try {
        const entityRef = techDocsPage?.entityRef;
        if (entityRef) {
          const { namespace, kind, name } = entityRef;
          const basePath = `/docs/${namespace}/${kind.toLowerCase()}/${name}/${versionValue}/`;
          router.push(basePath);
        }
      } catch (_) {
        // ignore
      }
    }
  };

  return (
    <div className={classes.container}>
      <FormControl variant="standard" size="small" className={classes.control}>
        <InputLabel>Doc Version:</InputLabel>
        <NativeSelect
          value={selected}
          onChange={e => handleVersionChange((e.target.value as string) || '')}
        >
          {versions.map(v => (
            <option key={v.value} value={v.value}>{v.label}</option>
          ))}
        </NativeSelect>
      </FormControl>
    </div>
  );
};
